import React, { useCallback, useMemo } from 'react';
import { CompositeDataSetResumeFetcherRecipe } from 'libs/end-to-end/src/types/databases/data-sets/composite-data-set-config';
import { CompositeDataSetResumeFetcherIntegrationType } from 'libs/end-to-end/src/types/databases/data-sets/composite-data-set-config';
import { CompositeDataSetResumeFetcherIntegrationConfig } from 'libs/end-to-end/src/types/databases/data-sets/composite-data-set-config';
import { CompositeDataSetRecipe } from 'libs/end-to-end/src/types/databases/data-sets/composite-data-set-config';
import { z } from 'zod';
import { DataSetCompositeRecipeDataWizardTracker } from 'apps/sigma2-ui/src/data-wizard/components/composite-dataset/recipes/DataSetCompositeRecipeDataWizardTracker';
import { DataSetCompositeRecipeDataWizardTrackerItem } from 'apps/sigma2-ui/src/data-wizard/components/composite-dataset/recipes/DataSetCompositeRecipeDataWizardTracker';
import { useDataSetCompositeRecipeDataWizardTracker } from 'apps/sigma2-ui/src/data-wizard/components/composite-dataset/recipes/DataSetCompositeRecipeDataWizardTracker';
import { DataSetCompositeCompleteIndicator } from 'apps/sigma2-ui/src/data-wizard/components/composite-dataset/recipes/DataSetCompositeCompleteIndicator';
import { Spacing } from 'apps/sigma2-ui/src/components/ui/Spacing';
import { WorkflowDataSetSelector } from 'apps/sigma2-ui/src/components/workflows/WorkflowDataSetSelector';
import { useSimpleReadPattern } from 'apps/sigma2-ui/src/lib/hooks/useSimpleReadPattern';
import { DataSets } from 'libs/end-to-end/src/types/databases/data-sets/data-sets';
import { getApiBaseUrl } from 'apps/sigma2-ui/src/constants/environment';
import { SelectBoxWithSearch } from 'apps/sigma2-ui/src/components/ui/SelectBoxWithSearch';
import { SimpleInput } from 'apps/sigma2-ui/src/components/ui/SimpleInput';
import { TaggedDataSetReference } from 'libs/end-to-end/src/types/databases/data-sets/data-set-reference';
import { DataSetSecretSelector } from 'apps/sigma2-ui/src/data-wizard/components/DataSetSecretSelector';
import { SecretReference } from 'libs/end-to-end/src/types/secrets/secret-reference';
import { useListOrganizationSecretsPattern } from 'apps/sigma2-ui/src/api/list-organization-secrets-pattern';

const ResumeFetcherConfigStep = z.enum(['config', 'complete']);

type ResumeFetcherConfigStep = z.infer<typeof ResumeFetcherConfigStep>;

export const DataSetCompositeRecipeResumeFetcherConfigComponent = ({
  organizationId,
  databaseId,
  config,
  setConfig,
  taggedDataSetReferences,
}: {
  organizationId: string;
  databaseId: string;
  config: CompositeDataSetResumeFetcherRecipe;
  setConfig: (config: CompositeDataSetRecipe) => void;
  taggedDataSetReferences: TaggedDataSetReference[];
}) => {
  const patternListDataSetsGetUrlFn = useCallback(() => {
    return `${getApiBaseUrl()}/organizations/${organizationId}/databases/${databaseId}/data-sets`;
  }, [organizationId, databaseId]);

  const listDataSetsPattern = useSimpleReadPattern({
    responseSchema: DataSets,
    canRead: true,
    getUrlFn: patternListDataSetsGetUrlFn,
  });

  const listSecretsPattern = useListOrganizationSecretsPattern({
    organizationId,
  });

  const updateIntegrationConfig = useCallback(
    (integrationConfigUpdate: Partial<CompositeDataSetResumeFetcherIntegrationConfig>) => {
      const currentIntegrationConfig = config.integrationConfig;
      setConfig({
        ...config,
        integrationConfig: {
          ...currentIntegrationConfig,
          ...integrationConfigUpdate,
        } as CompositeDataSetResumeFetcherIntegrationConfig,
      });
    },
    [config, setConfig],
  );

  const updateUsernameSecretReference = useCallback(
    (authUsernameSecretReference: SecretReference | null) => {
      if (authUsernameSecretReference) {
        updateIntegrationConfig({
          authUsernameSecretReference,
        });
      }
    },
    [updateIntegrationConfig],
  );

  const updatePasswordSecretReference = useCallback(
    (authPasswordSecretReference: SecretReference | null) => {
      if (authPasswordSecretReference) {
        updateIntegrationConfig({
          authPasswordSecretReference,
        });
      }
    },
    [updateIntegrationConfig],
  );

  const isWorkdayIntegration = useMemo(() => {
    return config.integrationConfig.integrationType === 'Workday ATS' || config.integrationConfig.integrationType === 'Workday HRIS';
  }, [config.integrationConfig.integrationType]);

  const stepItems: DataSetCompositeRecipeDataWizardTrackerItem<ResumeFetcherConfigStep>[] =
    useMemo(() => {
      return [
        { value: 'config', display: 'Configuration' },
        { value: 'complete', display: 'Complete' },
      ];
    }, []);

  const dataWizardPayload =
    useDataSetCompositeRecipeDataWizardTracker<ResumeFetcherConfigStep>({
      items: stepItems,
      onNext: () => true,
    });

  const currentUiToShow = useMemo(() => {
    if (dataWizardPayload.selectedItem === null) {
      return null;
    }

    switch (dataWizardPayload.selectedItem) {
      case 'config':
        return (
          <Spacing size={'md'} direction={'vertical'}>
            <SelectBoxWithSearch
              type={'single'}
              setSelected={(value) => {
                const integrationType = value as CompositeDataSetResumeFetcherIntegrationType;
                if (integrationType === 'Workday ATS') {
                  setConfig({
                    ...config,
                    integrationConfig: {
                      type: 'workday-candidates',
                      integrationType: 'Workday ATS',
                      workdaySoapVersion: '',
                      endpoint: '',
                      authUsernameSecretReference: { secretId: '' },
                      authPasswordSecretReference: { secretId: '' },
                      fileNamePrefix: 'workday_candidates_',
                      remoteIdColumnIndex: 0,
                    },
                  });
                } else if (integrationType === 'Workday HRIS') {
                  setConfig({
                    ...config,
                    integrationConfig: {
                      type: 'workday-employees',
                      integrationType: 'Workday HRIS',
                      workdaySoapVersion: '',
                      endpoint: '',
                      authUsernameSecretReference: { secretId: '' },
                      authPasswordSecretReference: { secretId: '' },
                      fileNamePrefix: 'workday_employees_',
                      remoteIdColumnIndex: 0,
                    },
                  });
                }
              }}
              selected={config.integrationConfig.integrationType}
              list={CompositeDataSetResumeFetcherIntegrationType.options.map((type) => ({
                optionLabel: type,
                optionValue: type,
              }))}
              label={'Integration Type'}
            />
            <WorkflowDataSetSelector
              title={'Dataset'}
              dataSetId={config.dataSetId}
              setDataSetId={(id: string) => {
                setConfig({
                  ...config,
                  dataSetId: id ? id : '',
                });
              }}
              filterFn={(dataSet) => {
                return taggedDataSetReferences.some((taggedDataSetReference) => taggedDataSetReference.dataSetReference.dataSetId === dataSet.id);
              }}
              listDataSetsPattern={listDataSetsPattern}
            />
            {isWorkdayIntegration && (
              <div className="space-y-4 p-4 bg-gray-50 rounded-lg">
                <h3 className="text-lg font-medium text-gray-900">
                  Workday Configuration
                </h3>
                <p className="text-sm text-gray-600">
                  Configure the connection settings for Workday SOAP API.
                </p>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Workday SOAP Version
                  </label>
                  <SimpleInput
                    label=""
                    value={config.integrationConfig.workdaySoapVersion ?? ''}
                    onChange={(value) => {
                      updateIntegrationConfig({
                        workdaySoapVersion: value,
                      });
                    }}
                    placeholder="e.g., v42.2"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Workday Endpoint URL
                  </label>
                  <SimpleInput
                    label=""
                    value={config.integrationConfig.endpoint ?? ''}
                    onChange={(value) => {
                      updateIntegrationConfig({
                        endpoint: value,
                      });
                    }}
                    placeholder="https://your-tenant.workday.com/..."
                  />
                </div>

                <DataSetSecretSelector
                  secretReference={config.integrationConfig.authUsernameSecretReference ?? null}
                  setSecretReference={updateUsernameSecretReference}
                  organizationId={organizationId}
                  label="Select the Workday username secret"
                  listSecretsPattern={listSecretsPattern}
                />

                <DataSetSecretSelector
                  secretReference={config.integrationConfig.authPasswordSecretReference ?? null}
                  setSecretReference={updatePasswordSecretReference}
                  organizationId={organizationId}
                  label="Select the Workday password secret"
                  listSecretsPattern={listSecretsPattern}
                />

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    File Name Prefix
                  </label>
                  <SimpleInput
                    label=""
                    value={config.integrationConfig.fileNamePrefix ?? ''}
                    onChange={(value) => {
                      updateIntegrationConfig({
                        fileNamePrefix: value,
                      });
                    }}
                    placeholder="e.g., workday_candidates_"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    The prefix used to identify files for this integration type
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Remote ID Column Index
                  </label>
                  <SimpleInput
                    label=""
                    value={config.integrationConfig.remoteIdColumnIndex?.toString() ?? '0'}
                    onChange={(value) => {
                      const numValue = parseInt(value, 10);
                      if (!isNaN(numValue)) {
                        updateIntegrationConfig({
                          remoteIdColumnIndex: numValue,
                        });
                      }
                    }}
                    placeholder="0"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    The column index (0-based) containing the remote ID in CSV files
                  </p>
                </div>
              </div>
            )}
          </Spacing>
        );
      case 'complete':
        return <DataSetCompositeCompleteIndicator />;
    }
  }, [
    config, 
    dataWizardPayload.selectedItem, 
    listDataSetsPattern, 
    listSecretsPattern,
    setConfig, 
    taggedDataSetReferences, 
    isWorkdayIntegration, 
    updateIntegrationConfig, 
    updateUsernameSecretReference, 
    updatePasswordSecretReference,
    organizationId,
  ]);

  return (
    <Spacing size={'lg'} direction={'vertical'}>
      <DataSetCompositeRecipeDataWizardTracker payload={dataWizardPayload} />
      {currentUiToShow}
      {dataWizardPayload.paginationButtons}
    </Spacing>
  );
};
